import React from 'react';
import { useRecommendations } from '../../hooks/useAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ErrorState, EmptyState } from './AnalyticsShared';
import { Target, TrendingUp, DollarSign, MousePointerClick } from 'lucide-react';

export function RecommendationsTab({ range }) {
  const { data, isLoading, error, refetch } = useRecommendations({ range });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (!data || !data.recommendations || data.recommendations.length === 0) {
    return (
      <EmptyState
        title="No recommendations available"
        description="Recommendations will appear here once you have enough data from your campaigns."
      />
    );
  }

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '—';
    return `$${(val / 100).toFixed(2)}`;
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined) return '—';
    return val.toLocaleString();
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'suggested_bid':
      case 'suggested_budget':
        return <DollarSign className="h-5 w-5" />;
      case 'expected_clicks':
        return <MousePointerClick className="h-5 w-5" />;
      case 'expected_orders':
        return <Target className="h-5 w-5" />;
      default:
        return <TrendingUp className="h-5 w-5" />;
    }
  };

  const getRecommendationColor = (type) => {
    switch (type) {
      case 'suggested_bid':
      case 'suggested_budget':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'expected_clicks':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'expected_orders':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getRecommendationTitle = (type) => {
    switch (type) {
      case 'suggested_bid':
        return 'Suggested Bid';
      case 'suggested_budget':
        return 'Suggested Budget';
      case 'expected_clicks':
        return 'Expected Clicks';
      case 'expected_orders':
        return 'Expected Orders';
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
  };

  return (
    <div className="space-y-6">
      {/* Introduction Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">AI-Powered Recommendations</h3>
              <p className="text-sm text-blue-800">
                Based on your performance data and market benchmarks, we've identified opportunities to optimize your campaigns.
                These recommendations are personalized to help you achieve better results.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations Grid */}
      <div className="grid grid-cols-1 gap-6">
        {data.recommendations.map((rec, index) => (
          <Card key={index} className={`border ${getRecommendationColor(rec.type)}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getRecommendationColor(rec.type)}`}>
                    {getRecommendationIcon(rec.type)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{getRecommendationTitle(rec.type)}</CardTitle>
                    {rec.listing_id && (
                      <p className="text-sm text-gray-600 mt-1">
                        For listing: {rec.listing_title || rec.listing_id}
                      </p>
                    )}
                  </div>
                </div>
                <Badge variant="secondary">{rec.confidence || 'Medium'} Confidence</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Value */}
              {rec.value !== undefined && (
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {rec.type.includes('bid') || rec.type.includes('budget') || rec.type.includes('cpc')
                      ? formatCurrency(rec.value)
                      : formatNumber(rec.value)}
                  </span>
                  {rec.current_value !== undefined && (
                    <span className="text-sm text-gray-500">
                      (currently {rec.type.includes('bid') || rec.type.includes('budget') || rec.type.includes('cpc')
                        ? formatCurrency(rec.current_value)
                        : formatNumber(rec.current_value)})
                    </span>
                  )}
                </div>
              )}

              {/* Description */}
              {rec.description && (
                <p className="text-sm text-gray-700">{rec.description}</p>
              )}

              {/* Expected Impact */}
              {rec.expected_impact && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-2">Expected Impact:</p>
                  <div className="grid grid-cols-2 gap-4">
                    {rec.expected_impact.clicks && (
                      <div>
                        <p className="text-xs text-gray-600">Clicks</p>
                        <p className="text-lg font-semibold text-gray-900">
                          +{formatNumber(rec.expected_impact.clicks)}
                        </p>
                      </div>
                    )}
                    {rec.expected_impact.orders && (
                      <div>
                        <p className="text-xs text-gray-600">Orders</p>
                        <p className="text-lg font-semibold text-gray-900">
                          +{formatNumber(rec.expected_impact.orders)}
                        </p>
                      </div>
                    )}
                    {rec.expected_impact.revenue_cents && (
                      <div>
                        <p className="text-xs text-gray-600">Revenue</p>
                        <p className="text-lg font-semibold text-gray-900">
                          +{formatCurrency(rec.expected_impact.revenue_cents)}
                        </p>
                      </div>
                    )}
                    {rec.expected_impact.roas && (
                      <div>
                        <p className="text-xs text-gray-600">ROAS</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {rec.expected_impact.roas.toFixed(2)}x
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reason/Rationale */}
              {rec.reason && (
                <p className="text-xs text-gray-600 italic">{rec.reason}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
